import * as React from 'react';
import * as A from 'fp-ts/Array';
import { Link } from 'react-router-dom';
import {
  FullMatchFragment,
  useCreateMatchEventMutation,
  useDeleteMatchEventMutation,
} from '../../../generated/graphql';
import { ListSelector } from '../../../components/ListSelector';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { useAlert } from '../../../contexts/AlertContext';
import { ButtonLink } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { PlayerEventEditList } from '../components/PlayerEventEditList';
import { paths } from '../../../paths';
import { PlayerNameWithAvatar } from '../components/PlayerNameWithAvatar';
import { useMatchStats } from './useMatchStats';

interface Props {
  match: FullMatchFragment;
}

export const MatchScorers: React.FC<Props> = ({ match }) => {
  const analytics = useAnalytics();
  const alert = useAlert();

  const { stats } = useMatchStats(match.id);

  const [createScorerResponse, createScorer] = useCreateMatchEventMutation();
  const [deleteScorerResponse, deleteScorer] = useDeleteMatchEventMutation();

  if (match.team.isCaptain && A.isEmpty(match.players)) {
    return (
      <p>
        <Link
          to={paths.match.settings.squad(match.id)}
          className="text-primary"
        >
          Set a team for this match
        </Link>{' '}
        to add scorers.
      </p>
    );
  }

  /**
   * If a squad has been selected for this match, only show those players.
   * Otherwise show the all the players in the team
   */
  const players = A.isEmpty(match.players) ? match.team.players : match.players;

  const setGoalScorer = (playerId: string) => {
    if (createScorerResponse.fetching) {
      return;
    }
    createScorer({
      input: { playerId, matchId: match.id, event: 'GOAL_SCORED' },
    }).then((response) => {
      const formEventMeta = { form: 'create_scorer' };
      if (response?.data?.createMatchEvent?.validation) {
        analytics.track('Data Submit Failed', formEventMeta);
        alert('Something went wrong', 'danger');
      }

      if (response?.data?.createMatchEvent?.entity) {
        analytics.track('Data Submit Succeeded', formEventMeta);
        alert('Goal scorer confirmed');
      }
    });
  };

  const removeGoalscorer = (scorerId: string) => {
    if (deleteScorerResponse.fetching) {
      return;
    }
    deleteScorer({ id: scorerId }).then((response) => {
      const formEventMeta = { form: 'delete_scorer' };
      if (response?.data?.deleteMatchEvent?.error) {
        analytics.track('Data Submit Failed', formEventMeta);
        alert('Something went wrong', 'danger');
        return;
      }
      if (response?.data?.deleteMatchEvent?.match) {
        analytics.track('Data Submit Succeeded', formEventMeta);
        alert('Goal scorer removed');
      }
    });
  };

  return (
    <div className="space-y-4">
      <Heading>Match scorers</Heading>
      {stats.scorers ? (
        <PlayerEventEditList
          playerEvents={stats.scorers}
          onRemoveEvent={removeGoalscorer}
          getButtonText={(p) => `Remove ${p.name}'s goal`}
          key="scorers"
        />
      ) : (
        <p>No scorers yet</p>
      )}

      <ListSelector
        label="Add goalscorers"
        onChange={setGoalScorer}
        value=""
        options={players.map((p) => ({
          name: <PlayerNameWithAvatar player={p} />,
          value: p.id,
          searchValue: p.name,
        }))}
        showFilter={players.length > 15}
      />

      <ButtonLink to={paths.match.home(match.id)} icon="arrow-left">
        Done
      </ButtonLink>
    </div>
  );
};
